import { ValidatorFn, Validators } from '@angular/forms';
import moment from 'moment';

export const DATE_FORMAT = 'DD/MM/YYYY';
export const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

export type FieldKind = 'select' | 'multiselect' | 'radio' | 'text' | 'textarea' | 'number' | 'boolean' | 'date' | 'datetime';

export interface FieldOption {
    label: string;
    value: any;
}

export interface WizardField {
    key: string;
    label: string;
    summaryTitle: string;
    kind: FieldKind;
    options: FieldOption[];
    validators: ValidatorFn[];
    required: boolean;
    goTo?: number[];
    default?: any;
    placeholder?: string;
}

const toOption = (option: any): FieldOption =>
    option !== null && typeof option === 'object'
        ? { label: option.label ?? String(option.value), value: option.value }
        : { label: String(option), value: option };

const resolveKind = (property: any, hasOptions: boolean): FieldKind => {
    const ui = property.uniforms ?? {};
    if (property.type === 'array' && hasOptions) return 'multiselect';
    if (hasOptions) return ui.checkboxes ? 'radio' : 'select';
    if (property.format === 'date') return 'date';
    if (property.format === 'date-time') return 'datetime';
    if (property.type === 'number' || property.type === 'integer') return 'number';
    if (property.type === 'boolean') return 'boolean';
    return ui.multiline || ui.component === 'LongTextField' || property.maxLength > 255 ? 'textarea' : 'text';
};

export function buildFields(schema: any): WizardField[] {
    const required = new Set<string>(schema?.required ?? []);
    return Object.entries<any>(schema?.properties ?? {}).map(([key, property]) => {
        const ui = property.uniforms ?? {};
        const rawOptions = property.allowedValues ?? ui.allowedValues ?? property.enum ?? property.items?.enum ?? ui.options ?? [];
        const options = rawOptions.map(toOption);
        const kind = resolveKind(property, options.length > 0);
        const isRequired = required.has(key) && kind !== 'boolean';

        const validators: ValidatorFn[] = [];
        if (isRequired) validators.push(Validators.required);
        if (property.minLength) validators.push(Validators.minLength(property.minLength));
        if (property.maxLength) validators.push(Validators.maxLength(property.maxLength));
        if (property.pattern) validators.push(Validators.pattern(property.pattern));
        if (property.minimum !== undefined) validators.push(Validators.min(property.minimum));
        if (property.maximum !== undefined) validators.push(Validators.max(property.maximum));

        const label = ui.label ?? ui.title ?? property.title ?? key;
        return {
            key,
            label,
            summaryTitle: ui.title ?? property.title ?? key,
            kind,
            options,
            validators,
            required: isRequired,
            goTo: ui.goTo,
            default: property.default,
            placeholder: ui.placeholder,
        };
    });
}

export const isEmptyValue = (value: any): boolean =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

// Stored values use the mobile app formats; native inputs need ISO-like strings.
export function toControlValue(kind: FieldKind, stored: any): any {
    if (kind === 'multiselect') return Array.isArray(stored) ? stored : [];
    if (kind === 'boolean') return !!stored;
    if (isEmptyValue(stored)) return null;
    if (kind === 'date') {
        const date = moment(stored, [DATE_FORMAT, moment.ISO_8601], true);
        return date.isValid() ? date.toDate() : null;
    }
    if (kind === 'datetime') {
        const date = moment(stored, [DATE_TIME_FORMAT, moment.ISO_8601], true);
        return date.isValid() ? date.format('DD-MM-YYYYTHH:mm') : null;
    }
    return stored;
}

export function fromControlValue(kind: FieldKind, value: any): any {
    if (isEmptyValue(value)) return null;
    if (kind === 'date') return moment(value, [moment.ISO_8601, DATE_FORMAT, 'DD-MM-YYYY', 'YYYY-MM-DD'], true).format(DATE_FORMAT);
    if (kind === 'datetime') return moment(value, 'DD-MM-YYYYTHH:mm').format(DATE_TIME_FORMAT);
    if (kind === 'number') return Number(value);
    return value;
}
